/**
 * Sessão do usuário.
 *
 * O access token vive em memória (`api/token.ts`) e a sessão é reconstruída na
 * carga da página pelo cookie `httpOnly` do refresh — o que dá continuidade sem
 * expor o token a XSS.
 *
 * A impersonação é tratada aqui porque é uma troca de sessão: o token do
 * superadmin fica guardado para que "voltar para minha conta" seja imediato,
 * sem novo login.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { ApiError, apiFetch } from "../api/client";
import { getAccessToken, setAccessToken } from "../api/token";

export interface TenantSummary {
  id: string;
  slug: string;
  name: string;
  primary_color: string;
  logo_url: string | null;
}

export interface SessionUser {
  id: string;
  email: string;
  full_name: string;
  role: "SUPERADMIN" | "TENANT_ADMIN" | "TENANT_EDITOR" | "TENANT_VIEWER";
  must_change_password: boolean;
  tenant: TenantSummary | null;
  impersonated_by: { id: string; full_name: string } | null;
}

interface TokenResponse {
  access_token: string;
  expires_in: number;
  user: SessionUser;
}

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  isImpersonating: boolean;
  login: (email: string, password: string, tenantSlug?: string) => Promise<SessionUser>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  startImpersonation: (token: string, user: SessionUser) => void;
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [superadminToken, setSuperadminToken] = useState<string | null>(null);

  const applySession = useCallback((token: string, sessionUser: SessionUser) => {
    setAccessToken(token);
    setUser(sessionUser);
  }, []);

  // Na carga da página, tenta reconstruir a sessão pelo cookie de refresh.
  // Falhar aqui é normal — significa apenas que ninguém está autenticado.
  useEffect(() => {
    let cancelado = false;

    apiFetch<TokenResponse>("/auth/refresh", { method: "POST" })
      .then((resposta) => {
        if (!cancelado) applySession(resposta.access_token, resposta.user);
      })
      .catch(() => {
        if (!cancelado) setAccessToken(null);
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });

    return () => {
      cancelado = true;
    };
  }, [applySession]);

  const login = useCallback(
    async (email: string, password: string, tenantSlug?: string) => {
      const resposta = await apiFetch<TokenResponse>("/auth/login", {
        method: "POST",
        body: { email, password, tenant_slug: tenantSlug ?? null },
      });
      applySession(resposta.access_token, resposta.user);
      return resposta.user;
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    try {
      await apiFetch<void>("/auth/logout", { method: "POST" });
    } catch {
      // Sessão já expirada no servidor: encerrar localmente basta.
    }
    setAccessToken(null);
    setSuperadminToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const atual = await apiFetch<SessionUser>("/auth/me");
    setUser(atual);
  }, []);

  const startImpersonation = useCallback(
    (token: string, impersonado: SessionUser) => {
      // Guarda o token do superadmin para a volta. Não é possível recuperá-lo
      // depois: ele só existe em memória.
      setSuperadminToken((anterior) => anterior ?? getAccessToken());
      applySession(token, impersonado);
    },
    [applySession],
  );

  const stopImpersonation = useCallback(async () => {
    if (superadminToken) {
      setAccessToken(superadminToken);
      setSuperadminToken(null);
      try {
        await refreshUser();
        return;
      } catch (erro) {
        // Token do superadmin expirou durante a impersonação (15 min é curto).
        if (!(erro instanceof ApiError) || erro.status !== 401) throw erro;
      }
    }
    // Sem token guardado ou já expirado: o cookie de refresh reconstrói a
    // sessão original, porque a impersonação nunca emitiu cookie próprio.
    const resposta = await apiFetch<TokenResponse>("/auth/refresh", { method: "POST" });
    applySession(resposta.access_token, resposta.user);
  }, [applySession, refreshUser, superadminToken]);

  const valor = useMemo<AuthState>(
    () => ({
      user,
      loading,
      isImpersonating: Boolean(user?.impersonated_by),
      login,
      logout,
      refreshUser,
      startImpersonation,
      stopImpersonation,
    }),
    [user, loading, login, logout, refreshUser, startImpersonation, stopImpersonation],
  );

  return <AuthContext.Provider value={valor}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const contexto = useContext(AuthContext);
  if (!contexto) {
    throw new Error("useAuth precisa estar dentro de <AuthProvider>");
  }
  return contexto;
}
